import { MapPin, Building2 } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../lib/api";

const COVERAGE = [
    {
        city: "Delhi NCR",
        tier: "tier1",
        markets: ["Tappal", "Sohna (Gurgaon)", "Noida Extension (Greater Noida West)", "Noida Sector 150", "Dwarka Expressway", "DLF Golf Course Road", "South Delhi", "Vasant Kunj", "New Gurugram", "Ghaziabad"],
    },
    {
        city: "Hyderabad",
        tier: "tier1",
        markets: ["Gachibowli", "Financial District", "Kokapet", "Kondapur", "Nanakramguda", "HITEC City", "Jubilee Hills", "Banjara Hills", "Shadnagar", "Narsingi"],
    },
    {
        city: "Bengaluru",
        tier: "tier1",
        markets: ["Whitefield", "ORR (Outer Ring Road)", "Sarjapur Road", "Electronic City", "HSR Layout", "Koramangala", "Indiranagar", "Marathahalli", "Devanahalli", "Hebbal"],
    },
    {
        city: "Chennai",
        tier: "tier1",
        markets: ["OMR (Old Mahabalipuram Road)", "Velachery", "Adyar", "Anna Nagar", "T Nagar", "Porur", "ECR (East Coast Road)", "Sholinganallur", "Nungambakkam", "Perambur"],
    },
];

export default function Coverage() {
    const [cityCount, setCityCount] = useState(COVERAGE.length);
    const [microMarketCount, setMicroMarketCount] = useState(20);
    const [verifiedCount, setVerifiedCount] = useState(5);

    useEffect(() => {
        api.get("/cpl/config").then(({ data }) => {
            const cities = data.cities || [];
            setCityCount(cities.length);
            const mm = cities.reduce((s, c) => s + (c.micro_markets || []).length, 0);
            setMicroMarketCount(mm);
        }).catch(() => {});
        api.get("/cpl/verified-results").then(({ data }) => setVerifiedCount((data || []).length)).catch(() => {});
    }, []);
    return (
        <section
            id="coverage"
            data-testid="coverage-section"
            className="relative py-24 sm:py-32 px-6 sm:px-10 lg:px-16 border-t border-white/5"
        >
            <div className="max-w-7xl mx-auto">
                <div className="grid lg:grid-cols-12 gap-12 items-end mb-16">
                    <div className="lg:col-span-7">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="h-px w-10 bg-[#D4AF37]" />
                            <span className="overline">Where We Operate</span>
                        </div>
                        <h2 className="font-serif-display text-4xl sm:text-5xl tracking-tight leading-[1.05]">
                            {cityCount} active markets. {microMarketCount}+ <br />
                            <span className="italic text-[#D4AF37]">micro-markets</span>. One partner.
                        </h2>
                        <p className="mt-6 text-white/70 leading-relaxed max-w-xl">
                            Buyer intent is hyper-local. That's why we run separate campaign machines for every
                            micro-market — from Tappal to Gachibowli. More Indian cities are being added to the
                            AI CPL engine as market data is confirmed.
                        </p>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="grid grid-cols-3 gap-4">
                            <StatBlock label="Active Markets" value={String(cityCount)} />
                            <StatBlock label="Micro-markets" value={`${microMarketCount}+`} gold />
                            <StatBlock label="Verified Campaigns" value={String(verifiedCount)} />
                        </div>
                    </div>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="coverage-grid">
                    {COVERAGE.map((c) => (
                        <div
                            key={c.city}
                            data-testid={`coverage-${c.city.replace(/\s/g, "-").toLowerCase()}`}
                            className="hol-card p-6"
                        >
                            <div className="flex items-start justify-between mb-5">
                                <div className="flex items-center gap-3">
                                    <Building2 size={16} className="text-[#D4AF37]" strokeWidth={1.5} />
                                    <h3 className="font-serif-display text-2xl">{c.city}</h3>
                                </div>
                                <span className={`text-[9px] uppercase tracking-widest px-2 py-1 border ${
                                    c.tier === "tier1"
                                        ? "border-[#D4AF37]/40 text-[#D4AF37]"
                                        : "border-white/20 text-white/50"
                                }`}>
                                    {c.tier === "tier1" ? "Tier-1" : "Tier-2 · Growing"}
                                </span>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {c.markets.map((m) => (
                                    <span
                                        key={m}
                                        className="text-[11px] px-2.5 py-1 bg-white/[0.04] text-white/70 hover:bg-[#D4AF37]/10 hover:text-[#D4AF37] transition-colors border border-white/5"
                                    >
                                        {m}
                                    </span>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-12 flex items-center gap-3 text-sm text-white/50">
                    <MapPin size={14} className="text-[#D4AF37]" />
                    <span>Don't see your city? We onboard new micro-markets within 14 days.{" "}
                        <a href="#lead" className="text-[#D4AF37] hover:underline">Request coverage →</a>
                    </span>
                </div>
            </div>
        </section>
    );
}

function StatBlock({ label, value, gold }) {
    return (
        <div className={`p-4 border ${gold ? "border-[#D4AF37]/40 bg-[#D4AF37]/5" : "border-white/10"}`}>
            <div className="font-serif-display text-3xl sm:text-4xl text-[#D4AF37]">{value}</div>
            <div className="overline mt-1 !text-white/50">{label}</div>
        </div>
    );
}
